// ImageInstance — atomic (filename, src, hash) bundle for the labeling
// pages.
//
// Why this exists: LabelPage used to keep filename, blob URL, hash, and
// "what the browser actually painted" in five separate state slots and
// six refs. Every code path that swapped images had to update all of
// them in lockstep, and a single missed update produced the drift bug
// where labels got attached to the wrong IC.
//
// An ImageInstance binds those values together at construction. The
// component holds at most three instances (`current`, `next`,
// `submitting`) and uses object identity (`===`) to ask questions like
// "is the painted image the same as the one we're submitting against?"
// Stale onLoad events from a previous image fire on a different
// instance — the identity check rejects them.
//
// Each instance owns its blob URL: dispose() revokes it. Cached
// instances created from a sessionStorage URL are still owned (the
// session cache only holds the URL string, not the underlying blob —
// once we revoke, the next cache hit's HEAD check fails and we
// re-fetch). This matches the pre-refactor behavior.

// SHA-256 (hex) of an ArrayBuffer or Blob, truncated to 32 chars. The
// audit-log row only needs collision-resistance across the few thousand
// ICs in the pool, not cryptographic guarantees.
export async function sha256Hex(blobOrBuffer) {
  const buf =
    blobOrBuffer instanceof ArrayBuffer
      ? blobOrBuffer
      : await blobOrBuffer.arrayBuffer();
  const digest = await crypto.subtle.digest("SHA-256", buf);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("")
    .slice(0, 32);
}

// crypto.randomUUID is widely available, but fall back for older
// browsers / Jest's jsdom < 18.
function makeId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `img_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

export class ImageInstance {
  // Use the named factories below; the constructor is internal.
  constructor({ filename, src, hash, kind, ownsBlobUrl }) {
    this.id = makeId();
    this.filename = filename;
    // `src` is whatever goes into <img src=...>: a blob:, data:, or
    // base64 URI. The instance doesn't care which.
    this.src = src;
    this.hash = hash || null;
    this.kind = kind; // "blob" | "cache" | "base64" — for debugging only
    this._ownsBlobUrl = !!ownsBlobUrl;
    this._disposed = false;
  }

  // Build from a freshly-fetched blob. Hashes and creates a blob URL.
  // The instance owns the blob URL and will revoke on dispose().
  static async fromBlob(filename, blob) {
    const blobUrl = URL.createObjectURL(blob);
    let hash = null;
    try {
      hash = await sha256Hex(blob);
    } catch (e) {
      // Hashing failures are non-fatal; the backend cross-check tolerates
      // an empty displayedHash. Log and continue.
      // eslint-disable-next-line no-console
      console.warn("ImageInstance.fromBlob hash failed:", e && e.message);
    }
    return new ImageInstance({
      filename,
      src: blobUrl,
      hash,
      kind: "blob",
      ownsBlobUrl: true,
    });
  }

  // Build from a sessionStorage cache hit. The cached URL came from a
  // previous fetch in this tab; we adopt ownership so dispose() revokes
  // it (matches pre-refactor disposal semantics — the cache becomes
  // self-invalidating once the URL is revoked, which the HEAD check on
  // retrieval already handles).
  //
  // If `hash` is unknown, callers can backfill it via
  // `await instance.ensureHashFromCache()`.
  static fromCachedUrl(filename, blobUrl, hash = null) {
    return new ImageInstance({
      filename,
      src: blobUrl,
      hash,
      kind: "cache",
      ownsBlobUrl: true,
    });
  }

  // Build from a base64-encoded payload (PracticePage uses this — it
  // doesn't need blob URL lifecycle management since it doesn't prefetch).
  static fromBase64(filename, base64String, mime = "image/png") {
    return new ImageInstance({
      filename,
      src: `data:${mime};base64,${base64String}`,
      hash: null,
      kind: "base64",
      ownsBlobUrl: false,
    });
  }

  // Re-fetch the blob via the cached URL and compute its hash. Use
  // when fromCachedUrl had no hash and we need one for submission.
  async ensureHashFromCache() {
    if (this.hash) return this.hash;
    if (this._disposed) return null;
    try {
      const resp = await fetch(this.src);
      const blob = await resp.blob();
      this.hash = await sha256Hex(blob);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("ensureHashFromCache failed:", e && e.message);
    }
    return this.hash;
  }

  // Revoke the blob URL (if owned) and mark disposed. Idempotent.
  dispose() {
    if (this._disposed) return;
    this._disposed = true;
    if (this._ownsBlobUrl && this.src && this.src.startsWith("blob:")) {
      URL.revokeObjectURL(this.src);
    }
  }

  get disposed() {
    return this._disposed;
  }
}
